import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  clearAuth,
  clearPostAuthRedirect,
  clearSuspiciousActivity,
  clearRedirectUrl,
  respondToSuspiciousActivity,
} from '../../store/slices/authSlice';
import { closeModal } from '../../store/slices/uiSlice';

const trackEvent = (name, extra = {}) => {
  try {
    window.dispatchEvent(
      new CustomEvent('analytics:event', {
        detail: { name, ...extra },
      })
    );
  } catch {
    // analytics is optional
  }
};

const isEligibleRole = (user) =>
  user?.user_type === 'JetSetter' || user?.user_type === 'Airliner';

const getFallbackRedirect = (user) => {
  if (user?.user_type === 'Airliner') return '/airliner/profile';
  if (user?.user_type === 'JetSetter') return '/jetsetter/profile/user-details';
  return '/';
};

export default function SuspiciousActivityModal() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const yesButtonRef = useRef(null);
  const [clickedAction, setClickedAction] = useState(null);

  const {
    isAuthenticated,
    user,
    suspiciousActivity,
    suspiciousActivityResponding,
    suspiciousActivityError,
    postAuthRedirect,
  } = useAppSelector((state) => state.auth);

  const isOpen = useMemo(
    () =>
      isAuthenticated &&
      isEligibleRole(user) &&
      Boolean(suspiciousActivity?.id),
    [isAuthenticated, user, suspiciousActivity?.id]
  );

  useEffect(() => {
    if (!isOpen) return;
    trackEvent('suspicious_modal_shown', { alert_id: suspiciousActivity?.id });
    yesButtonRef.current?.focus();
  }, [isOpen, suspiciousActivity?.id]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
    return undefined;
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const finalizeYesFlow = () => {
    const target = postAuthRedirect || getFallbackRedirect(user);
    dispatch(clearPostAuthRedirect());
    dispatch(clearRedirectUrl());
    dispatch(closeModal('login'));
    if (target && target !== window.location.pathname) {
      navigate(target, { replace: true });
    }
  };

  const handleAction = async (response) => {
    setClickedAction(response);
    if (response === 'yes') {
      trackEvent('suspicious_modal_yes_clicked', { alert_id: suspiciousActivity.id });
    } else {
      trackEvent('suspicious_modal_no_clicked', { alert_id: suspiciousActivity.id });
    }

    try {
      const result = await dispatch(
        respondToSuspiciousActivity({
          alert_id: suspiciousActivity.id,
          response,
        })
      ).unwrap();

      trackEvent('suspicious_modal_response_success', {
        alert_id: suspiciousActivity.id,
        response,
      });

      if (response === 'yes') {
        toast.success(result?.message || 'Thank you for confirming.');
        dispatch(clearSuspiciousActivity());
        finalizeYesFlow();
      } else {
        toast.success(
          result?.message ||
            'Your account was secured. Please reset your password from the email we sent.'
        );
        dispatch(clearAuth());
        dispatch(clearSuspiciousActivity());
        dispatch(clearPostAuthRedirect());
        navigate('/?login=1', { replace: true });
      }
    } catch (error) {
      trackEvent('suspicious_modal_response_failed', {
        alert_id: suspiciousActivity.id,
        response,
      });
    } finally {
      setClickedAction(null);
    }
  };

  const yesLoading = suspiciousActivityResponding && clickedAction === 'yes';
  const noLoading = suspiciousActivityResponding && clickedAction === 'no';

  return (
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="suspicious-signin-title"
      aria-describedby="suspicious-signin-description"
    >
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl p-6">
        <h2 id="suspicious-signin-title" className="text-xl font-bold text-gray-900 m-0">
          Unusual sign-in detected
        </h2>
        <p id="suspicious-signin-description" className="mt-3 text-sm text-gray-700 mb-0">
          {suspiciousActivity?.message || 'We noticed unusual activity on your account.'}
        </p>
        <p className="mt-2 text-sm text-gray-600">
          Please confirm if this sign-in was you.
        </p>

        {suspiciousActivityError ? (
          <p className="mt-3 text-sm text-red-600" role="alert">
            {suspiciousActivityError}
          </p>
        ) : null}

        <div className="mt-6 flex flex-col sm:flex-row sm:justify-end gap-2">
          <button
            ref={yesButtonRef}
            type="button"
            onClick={() => handleAction('yes')}
            disabled={suspiciousActivityResponding}
            className="px-4 py-2.5 rounded-md border border-gray-300 bg-white text-gray-900 text-sm font-semibold hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {yesLoading ? 'Please wait...' : 'Yes, it was me'}
          </button>
          <button
            type="button"
            onClick={() => handleAction('no')}
            disabled={suspiciousActivityResponding}
            className="px-4 py-2.5 rounded-md bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {noLoading ? 'Please wait...' : 'No, secure my account'}
          </button>
        </div>
      </div>
    </div>
  );
}

